/* eslint-disable no-unused-vars */
// =============================================================================
// VideoEditorScreen.js — FULL REBUILD
// JobEng — Business / Marketing
// Location: app/screens/business/marketing/VideoEditorScreen.js
//
// Tabs: Projects · Editor · Studio · Text · Audio · Export
// Backend: /api/data/design_assets (type: video_project), company-scoped,
//          AsyncStorage fallback video_projects_<bid>, campaign_content_<cid>
// AI: AIBrainService (useAI / AIChatWidget) — script, captions, hashtags
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import { useVideoPlayer, VideoView } from 'expo-video';
import * as Sharing from 'expo-sharing';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Image,
  Dimensions,
  ActivityIndicator,
  Alert,
  Modal,
  RefreshControl,
  Share,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

const { width: SCREEN_W } = Dimensions.get('window');

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok = await AsyncStorage.getItem('auth_token').catch(() => null);
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

// =============================================================================
// CONSTANTS
// =============================================================================

const C = {
  primary: '#0e4e96',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  dark: '#0F172A',
  darkCard: '#1E293B',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
};

const TABS = ['Projects', 'Editor', 'Studio', 'Text', 'Audio', 'Export'];

const ASPECT_RATIOS = [
  { key: '9:16', label: 'Story / Reels', w: 9, h: 16 },
  { key: '1:1', label: 'Square', w: 1, h: 1 },
  { key: '4:5', label: 'Portrait', w: 4, h: 5 },
  { key: '16:9', label: 'Landscape', w: 16, h: 9 },
];

const TEMPLATES = [
  { id: 't1', name: 'Project Showcase', duration: '2:45', icon: 'office-building', sections: ['Intro & logo', 'Before shots', 'Progress timelapse', 'Final reveal', 'Call to action'] },
  { id: 't2', name: 'Company Intro', duration: '1:30', icon: 'domain', sections: ['Logo animation', 'Team & values', 'Services', 'Contact details'] },
  { id: 't3', name: 'Job Advert', duration: '0:45', icon: 'briefcase', sections: ['Hook — the role', 'Why join us', 'Requirements', 'Apply on JobEng'] },
  { id: 't4', name: 'Client Testimonial', duration: '1:00', icon: 'account-voice', sections: ['Client intro', 'The problem', 'Our solution', 'Results & quote'] },
  { id: 't5', name: 'Quick Promo', duration: '0:30', icon: 'flash', sections: ['Attention hook', 'Offer', 'Urgency + CTA'] },
  { id: 't6', name: 'Event Recap', duration: '1:20', icon: 'calendar-star', sections: ['Event opener', 'Highlights montage', 'Key moments', 'Thank you + next event'] },
];

const STOCK_CLIPS = [
  { id: 's1', type: 'video', title: 'Construction site aerial', duration: '0:12', thumbnail: 'https://picsum.photos/seed/jobeng1/300/200' },
  { id: 's2', type: 'video', title: 'Office teamwork', duration: '0:08', thumbnail: 'https://picsum.photos/seed/jobeng2/300/200' },
  { id: 's3', type: 'image', title: 'Engineering blueprint', duration: '0:05', thumbnail: 'https://picsum.photos/seed/jobeng3/300/200' },
  { id: 's4', type: 'video', title: 'City skyline timelapse', duration: '0:10', thumbnail: 'https://picsum.photos/seed/jobeng4/300/200' },
  { id: 's5', type: 'image', title: 'Handshake close-up', duration: '0:05', thumbnail: 'https://picsum.photos/seed/jobeng5/300/200' },
  { id: 's6', type: 'video', title: 'Machinery at work', duration: '0:09', thumbnail: 'https://picsum.photos/seed/jobeng6/300/200' },
];

const FILTERS = [
  { id: 'none', name: 'None', tint: 'transparent', op: 0 },
  { id: 'vivid', name: 'Vivid', tint: '#FF6B00', op: 0.08 },
  { id: 'mono', name: 'Mono', tint: '#000000', op: 0.35 },
  { id: 'warm', name: 'Warm', tint: '#FFB347', op: 0.15 },
  { id: 'cool', name: 'Cool', tint: '#4A90D9', op: 0.15 },
  { id: 'vintage', name: 'Vintage', tint: '#8B6F47', op: 0.2 },
  { id: 'dramatic', name: 'Dramatic', tint: '#1A1A2E', op: 0.25 },
];

const TRANSITIONS = ['fade', 'slide', 'zoom', 'wipe', 'dissolve', 'none'];

const TEXT_POSITIONS = ['top', 'middle', 'bottom'];
const TEXT_ANIMATIONS = ['None', 'Fade In', 'Slide Up', 'Pop', 'Typewriter'];
const TEXT_COLORS = ['#FFFFFF', '#0e4e96', '#EC4899', '#10B981', '#F59E0B', '#000000'];
const TEXT_SIZES = [14, 18, 24, 32, 40];

const AUDIO_TRACKS = [
  { id: 'a1', name: 'Corporate Upbeat', genre: 'Corporate', duration: '2:30', bpm: 120, icon: 'music-note' },
  { id: 'a2', name: 'Inspiring Piano', genre: 'Cinematic', duration: '3:15', bpm: 80, icon: 'piano' },
  { id: 'a3', name: 'Tech Innovation', genre: 'Electronic', duration: '2:45', bpm: 128, icon: 'robot' },
  { id: 'a4', name: 'Construction Beat', genre: 'Industrial', duration: '2:00', bpm: 110, icon: 'hammer' },
  { id: 'a5', name: 'Ambient Flow', genre: 'Ambient', duration: '4:00', bpm: 90, icon: 'waves' },
  { id: 'a6', name: 'Success Story', genre: 'Motivational', duration: '3:30', bpm: 100, icon: 'trophy' },
  { id: 'a7', name: 'Engineering Pride', genre: 'Orchestral', duration: '2:50', bpm: 95, icon: 'cog' },
  { id: 'a8', name: 'Team Spirit', genre: 'Pop', duration: '2:20', bpm: 115, icon: 'account-group' },
];

const SFX = ['Whoosh', 'Pop', 'Click', 'Ding', 'Riser', 'Impact', 'Swipe', 'Sparkle'];

const EXPORT_QUALITIES = ['720p', '1080p', '4K'];
const EXPORT_FORMATS = ['mp4', 'mov', 'gif'];

const CLIP_SECONDS = 5; // default seconds per clip in the simulated timeline

// =============================================================================
// HELPERS
// =============================================================================

const durToSec = (d) => {
  if (typeof d === 'number') return d;
  if (typeof d === 'string' && d.includes(':')) {
    const [m, s] = d.split(':').map((n) => parseInt(n, 10) || 0);
    return m * 60 + s;
  }
  return CLIP_SECONDS;
};

const secToDur = (s) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const clipLength = (clip) => {
  const base = durToSec(clip.duration);
  const start = Number(clip.trimStart) || 0;
  const end = Number(clip.trimEnd) || base;
  return Math.max(1, Math.min(end, base) - start);
};

const totalDuration = (clips) => clips.reduce((s, c) => s + clipLength(c), 0);

// =============================================================================
// MODULE-SCOPE SUB-COMPONENTS (React.memo — no remount-per-keystroke)
// =============================================================================

const CompanyLogo = React.memo(({ uri, size = 32 }) => {
  if (uri) {
    return (
      <Image
        source={{ uri }}
        style={{ width: size, height: size, borderRadius: 6 }}
        onError={() => {}}
        resizeMode="contain"
      />
    );
  }
  return (
    <View style={{ width: size, height: size, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name="office-building" size={size * 0.6} color="#fff" />
    </View>
  );
});

const Chip = React.memo(({ label, active, onPress, small }) => (
  <TouchableOpacity
    style={[st.chip, small && st.chipSmall, active && st.chipActive]}
    onPress={onPress}
    activeOpacity={0.7}
  >
    <Text style={[st.chipText, small && { fontSize: 11 }, active && st.chipTextActive]}>
      {label}
    </Text>
  </TouchableOpacity>
));

const Stepper = React.memo(({ label, value, onChange, min = 0, max = 200, step = 10, suffix = '%' }) => (
  <View style={st.stepperRow}>
    <Text style={st.stepperLabel}>{label}</Text>
    <View style={st.stepperControls}>
      <TouchableOpacity
        style={st.stepperBtn}
        onPress={() => onChange(Math.max(min, value - step))}
      >
        <Ionicons name="remove" size={16} color={C.primary} />
      </TouchableOpacity>
      <Text style={st.stepperValue}>
        {value}
        {suffix}
      </Text>
      <TouchableOpacity
        style={st.stepperBtn}
        onPress={() => onChange(Math.min(max, value + step))}
      >
        <Ionicons name="add" size={16} color={C.primary} />
      </TouchableOpacity>
    </View>
  </View>
));

const SectionTitle = React.memo(({ children, right }) => (
  <View style={st.sectionTitleRow}>
    <Text style={st.sectionTitle}>{children}</Text>
    {right || null}
  </View>
));

const ProjectCard = React.memo(({ project, onOpen, onRename, onDuplicate, onDelete }) => {
  const clipCount =
    project.clipCount ??
    (Array.isArray(project.clips) ? project.clips.length : Number(project.clips) || 0);
  return (
    <View style={st.projectCard}>
      <TouchableOpacity style={st.projectMain} onPress={() => onOpen(project)} activeOpacity={0.7}>
        <View style={st.projectThumb}>
          {project.clips?.[0]?.thumbnail || project.clips?.[0]?.uri ? (
            <Image
              source={{ uri: project.clips[0].thumbnail || project.clips[0].uri }}
              style={{ width: '100%', height: '100%', borderRadius: 10 }}
              onError={() => {}}
            />
          ) : (
            <MaterialCommunityIcons name="movie-open-outline" size={26} color="#fff" />
          )}
        </View>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={st.projectTitle} numberOfLines={1}>{project.title}</Text>
          <View style={st.projectMetaRow}>
            <MaterialCommunityIcons name="filmstrip" size={13} color={C.sub} />
            <Text style={st.projectMeta}>{clipCount} clips</Text>
            <MaterialCommunityIcons name="clock-outline" size={13} color={C.sub} style={{ marginLeft: 8 }} />
            <Text style={st.projectMeta}>{project.duration || '0:00'}</Text>
            <MaterialCommunityIcons name="calendar-outline" size={13} color={C.sub} style={{ marginLeft: 8 }} />
            <Text style={st.projectMeta}>{new Date(project.lastEdited).toLocaleDateString()}</Text>
          </View>
          <View style={[st.statusPill, { backgroundColor: project.status === 'completed' ? '#D1FAE5' : '#F1F5F9' }]}>
            <Text style={[st.statusPillText, { color: project.status === 'completed' ? C.success : C.sub }]}>
              {project.status === 'completed' ? 'Completed' : 'Draft'}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
      <View style={st.projectActions}>
        <TouchableOpacity style={st.projIconBtn} onPress={() => onRename(project)}>
          <Ionicons name="pencil-outline" size={16} color={C.primary} />
        </TouchableOpacity>
        <TouchableOpacity style={st.projIconBtn} onPress={() => onDuplicate(project)}>
          <Ionicons name="copy-outline" size={16} color={C.sub} />
        </TouchableOpacity>
        <TouchableOpacity style={st.projIconBtn} onPress={() => onDelete(project)}>
          <Ionicons name="trash-outline" size={16} color={C.danger} />
        </TouchableOpacity>
      </View>
    </View>
  );
});

const TemplateCard = React.memo(({ template, active, onPress }) => (
  <TouchableOpacity
    style={[st.templateCard, active && st.templateCardActive]}
    onPress={() => onPress(template)}
    activeOpacity={0.7}
  >
    <MaterialCommunityIcons
      name={template.icon}
      size={22}
      color={active ? '#fff' : C.primary}
    />
    <Text style={[st.templateName, active && { color: '#fff' }]} numberOfLines={1}>
      {template.name}
    </Text>
    <Text style={[st.templateDur, active && { color: 'rgba(255,255,255,0.7)' }]}>
      {template.duration} · {template.sections.length} sections
    </Text>
  </TouchableOpacity>
));

const MediaClipCard = React.memo(({ clip, selected, onPress }) => (
  <TouchableOpacity
    style={[st.mediaClip, selected && st.mediaClipSelected]}
    onPress={() => onPress(clip)}
    activeOpacity={0.7}
  >
    <View style={st.mediaThumb}>
      {clip.thumbnail || clip.uri ? (
        <Image
          source={{ uri: clip.thumbnail || clip.uri }}
          style={{ width: '100%', height: '100%' }}
          onError={() => {}}
        />
      ) : null}
      {clip.type === 'video' && (
        <View style={st.mediaPlayBadge}>
          <MaterialCommunityIcons name="play" size={14} color="#fff" />
        </View>
      )}
      <View style={st.mediaDurBadge}>
        <Text style={st.mediaDurText}>{clip.duration || '0:05'}</Text>
      </View>
      {selected && (
        <View style={st.mediaCheck}>
          <MaterialCommunityIcons name="check-circle" size={20} color={C.primaryLight} />
        </View>
      )}
    </View>
    <Text style={st.mediaTitle} numberOfLines={1}>{clip.title}</Text>
  </TouchableOpacity>
));

const TimelineClip = React.memo(({ clip, index, total, onMoveLeft, onMoveRight, onTrim, onTransition, onDuplicate, onRemove }) => (
  <View style={st.tlClip}>
    <View style={st.tlThumb}>
      {clip.thumbnail || clip.uri ? (
        <Image source={{ uri: clip.thumbnail || clip.uri }} style={{ width: '100%', height: '100%' }} onError={() => {}} />
      ) : (
        <MaterialCommunityIcons name="image" size={18} color="#64748B" />
      )}
      <View style={st.tlIndex}><Text style={st.tlIndexText}>{index + 1}</Text></View>
    </View>
    <Text style={st.tlTitle} numberOfLines={1}>{clip.title}</Text>
    <Text style={st.tlDur}>{secToDur(clipLength(clip))}{clip.trimStart || clip.trimEnd ? ' ✂︎' : ''}</Text>
    {clip.transition && clip.transition !== 'none' && index < total - 1 && (
      <Text style={st.tlTransition}>→ {clip.transition}</Text>
    )}
    <View style={st.tlBtns}>
      <TouchableOpacity style={st.tlBtn} disabled={index === 0} onPress={() => onMoveLeft(index)}>
        <Ionicons name="chevron-back" size={13} color={index === 0 ? '#CBD5E1' : C.primary} />
      </TouchableOpacity>
      <TouchableOpacity style={st.tlBtn} onPress={() => onTrim(clip)}>
        <MaterialCommunityIcons name="scissors-cutting" size={13} color={C.warning} />
      </TouchableOpacity>
      <TouchableOpacity style={st.tlBtn} onPress={() => onTransition(clip)}>
        <MaterialCommunityIcons name="transition" size={13} color={C.accent} />
      </TouchableOpacity>
      <TouchableOpacity style={st.tlBtn} onPress={() => onDuplicate(index)}>
        <Ionicons name="copy-outline" size={12} color={C.sub} />
      </TouchableOpacity>
      <TouchableOpacity style={st.tlBtn} onPress={() => onRemove(index)}>
        <Ionicons name="close" size={13} color={C.danger} />
      </TouchableOpacity>
      <TouchableOpacity style={st.tlBtn} disabled={index === total - 1} onPress={() => onMoveRight(index)}>
        <Ionicons name="chevron-forward" size={13} color={index === total - 1 ? '#CBD5E1' : C.primary} />
      </TouchableOpacity>
    </View>
  </View>
));

const OverlayCard = React.memo(({ overlay, onEdit, onDelete }) => (
  <View style={st.overlayCard}>
    <View style={{ flex: 1 }}>
      <Text style={[st.overlayText, { color: overlay.color === '#FFFFFF' ? C.text : overlay.color }]} numberOfLines={1}>
        "{overlay.text}"
      </Text>
      <Text style={st.overlayMeta}>
        {overlay.position} · {overlay.size}px · {overlay.animation} · {overlay.start}s for {overlay.duration}s
      </Text>
    </View>
    <TouchableOpacity style={st.projIconBtn} onPress={() => onEdit(overlay)}>
      <Ionicons name="pencil-outline" size={15} color={C.primary} />
    </TouchableOpacity>
    <TouchableOpacity style={st.projIconBtn} onPress={() => onDelete(overlay.id)}>
      <Ionicons name="trash-outline" size={15} color={C.danger} />
    </TouchableOpacity>
  </View>
));

const TrackCard = React.memo(({ track, active, onPress }) => (
  <TouchableOpacity
    style={[st.trackCard, active && st.trackCardActive]}
    onPress={() => onPress(track)}
    activeOpacity={0.7}
  >
    <View style={[st.trackIcon, active && { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
      <MaterialCommunityIcons name={track.icon} size={18} color={active ? '#fff' : C.primary} />
    </View>
    <View style={{ flex: 1, marginLeft: 10 }}>
      <Text style={[st.trackName, active && { color: '#fff' }]}>{track.name}</Text>
      <Text style={[st.trackMeta, active && { color: 'rgba(255,255,255,0.7)' }]}>
        {track.genre} · {track.duration} · {track.bpm} BPM
      </Text>
    </View>
    {active && <MaterialCommunityIcons name="check-circle" size={20} color="#fff" />}
  </TouchableOpacity>
));

const Toggle = React.memo(({ label, value, onChange }) => (
  <TouchableOpacity style={st.toggleRow} onPress={() => onChange(!value)} activeOpacity={0.7}>
    <Text style={st.stepperLabel}>{label}</Text>
    <View style={[st.toggleTrack, value && { backgroundColor: C.success }]}>
      <View style={[st.toggleThumb, value && { alignSelf: 'flex-end' }]} />
    </View>
  </TouchableOpacity>
));

// =============================================================================
// MAIN SCREEN
// =============================================================================

const VideoEditorScreen = ({ navigation, route }) => {
  const campaignId = route?.params?.campaignId || null;
  const campaign = route?.params?.campaign || null;

  // ----- company / data -----
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [businessId, setBusinessId] = useState(0);
  const [projects, setProjects] = useState([]);
  const [savedAssets, setSavedAssets] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  // ----- AI -----
  const { ask: askAI, loading: aiLoading } = useAI('VideoEditorScreen');
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiWriting, setAiWriting] = useState(false);

  // ----- editor state -----
  const [activeTab, setActiveTab] = useState('Projects');
  const [selectedProject, setSelectedProject] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [selectedClips, setSelectedClips] = useState([]);
  const [uploadedClips, setUploadedClips] = useState([]);
  const [mediaFilter, setMediaFilter] = useState('All');
  const [dirty, setDirty] = useState(false);

  // ----- studio -----
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [defaultTransition, setDefaultTransition] = useState('fade');
  const [muted, setMuted] = useState(false);

  // ----- text overlays -----
  const [textOverlays, setTextOverlays] = useState([]);
  const [overlayModal, setOverlayModal] = useState(false);
  const [overlayForm, setOverlayForm] = useState(null);

  // ----- audio -----
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [selectedSfx, setSelectedSfx] = useState([]);
  const [musicVolume, setMusicVolume] = useState(80);
  const [originalVolume, setOriginalVolume] = useState(100);
  const [fadeIn, setFadeIn] = useState(true);
  const [fadeOut, setFadeOut] = useState(true);

  // ----- export -----
  const [exportQuality, setExportQuality] = useState('1080p');
  const [exportFormat, setExportFormat] = useState('mp4');
  const [watermark, setWatermark] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [scriptModal, setScriptModal] = useState(false);
  const [scriptText, setScriptText] = useState('');

  // ----- modals -----
  const [trimModal, setTrimModal] = useState(false);
  const [trimClip, setTrimClip] = useState(null);
  const [trimStart, setTrimStart] = useState('0');
  const [trimEnd, setTrimEnd] = useState('5');
  const [transitionModal, setTransitionModal] = useState(false);
  const [transitionClip, setTransitionClip] = useState(null);
  const [renameModal, setRenameModal] = useState(false);
  const [renameProject, setRenameProject] = useState(null);
  const [renameText, setRenameText] = useState('');

  const playTimer = useRef(null);

  // ---------------------------------------------------------------------------
  // LOAD: company profile + projects
  // ---------------------------------------------------------------------------

  const parseAssetToProject = useCallback((a) => {
    let d = {};
    try { d = typeof a.data === 'string' ? JSON.parse(a.data) : (a.data || {}); } catch {}
    const clips = Array.isArray(d.clips) ? d.clips : [];
    return {
      id: a.id,
      title: a.name || d.title || 'Untitled',
      description: d.description || '',
      template: d.template || null,
      clips,
      clipCount: clips.length,
      status: a.status || d.status || 'draft',
      duration: d.duration || secToDur(totalDuration(clips)),
      lastEdited: a.updated_at || a.created_at || new Date().toISOString(),
      campaign_id: a.campaign_id || null,
      raw: d,
      isLocal: String(a.id).startsWith('vid_'),
    };
  }, []);

  const loadProjects = useCallback(async (spinner = true) => {
    if (spinner) setLoadingProjects(true);
    const bid = parseInt(await AsyncStorage.getItem('company_id').catch(() => '0'), 10) || 0;
    setBusinessId(bid);
    if (!bid) { setProjects([]); setLoadingProjects(false); setRefreshing(false); return; }
    let loaded = false;
    try {
      const h = await getH();
      const res = await fetch(
        `${API_BASE}/api/data/design_assets?company_id=${bid}&type=video_project`,
        { headers: h }
      ).then((r) => r.json()).catch(() => null);
      if (res?.success && Array.isArray(res.data)) {
        setProjects(res.data.map(parseAssetToProject).sort((a, b) => String(b.lastEdited).localeCompare(String(a.lastEdited))));
        setSavedAssets(res.data);
        loaded = true;
      }
    } catch {}
    if (!loaded) {
      try {
        const local = await AsyncStorage.getItem(`video_projects_${bid}`).catch(() => null);
        if (local) {
          const parsed = JSON.parse(local);
          if (Array.isArray(parsed)) setProjects(parsed.map(parseAssetToProject));
        }
      } catch {}
    }
    setLoadingProjects(false);
    setRefreshing(false);
  }, [parseAssetToProject]);

  useEffect(() => {
    (async () => {
      // company profile: cache first, then API
      const bid = parseInt(await AsyncStorage.getItem('company_id').catch(() => '0'), 10) || 0;
      try {
        const raw = await AsyncStorage.getItem('company_profile');
        if (raw) {
          const p = JSON.parse(raw);
          const logo = p.logo || p.logo_url || p.company_logo;
          if (logo) setCompanyLogo(logo.startsWith('http') ? logo : `${API_BASE}${logo}`);
          setCompanyName(p.name || p.company_name || '');
        }
      } catch {}
      if (bid) {
        try {
          const h = await getH();
          const pr = await fetch(`${API_BASE}/api/company-profile/${bid}`, { headers: h })
            .then((r) => r.json()).catch(() => null);
          const p = pr?.data || pr;
          if (p) {
            if (p.logo) setCompanyLogo(p.logo.startsWith('http') ? p.logo : `${API_BASE}${p.logo}`);
            if (p.name || p.company_name) setCompanyName(p.name || p.company_name);
          }
        } catch {}
      }
      loadProjects();
    })();
  }, [loadProjects]);

  // ---------------------------------------------------------------------------
  // PLAYBACK SIMULATION
  // ---------------------------------------------------------------------------

  const timelineTotal = useMemo(() => Math.max(1, totalDuration(selectedClips)), [selectedClips]);

  useEffect(() => {
    if (isPlaying) {
      playTimer.current = setInterval(() => {
        setCurrentTime((t) => {
          const next = t + 0.25 * playbackSpeed;
          if (next >= timelineTotal) { setIsPlaying(false); return 0; }
          return next;
        });
      }, 250);
    }
    return () => { if (playTimer.current) clearInterval(playTimer.current); };
  }, [isPlaying, playbackSpeed, timelineTotal]);

  const currentClipIndex = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < selectedClips.length; i++) {
      acc += clipLength(selectedClips[i]);
      if (currentTime < acc) return i;
    }
    return Math.max(0, selectedClips.length - 1);
  }, [currentTime, selectedClips]);

  const activeOverlays = useMemo(
    () => textOverlays.filter((o) => currentTime >= o.start && currentTime <= o.start + o.duration),
    [textOverlays, currentTime]
  );

  // ---------------------------------------------------------------------------
  // REAL VIDEO PREVIEW (expo-video) — plays actual uploaded/camera video clips
  // ---------------------------------------------------------------------------

  const previewClip = selectedClips[currentClipIndex] || null;
  const videoSource =
    previewClip?.type === 'video' && previewClip?.uri ? previewClip.uri : null;

  const player = useVideoPlayer(videoSource, (p) => {
    p.loop = false;
    p.muted = muted;
  });

  useEffect(() => {
    try {
      if (videoSource) {
        const r = player.replaceAsync ? player.replaceAsync(videoSource) : player.replace(videoSource);
        if (r?.catch) r.catch(() => {});
      }
    } catch {}
  }, [videoSource, player]);

  useEffect(() => {
    try {
      if (isPlaying && videoSource) player.play();
      else player.pause();
    } catch {}
  }, [isPlaying, videoSource, player]);

  useEffect(() => {
    try { player.playbackRate = playbackSpeed; } catch {}
  }, [playbackSpeed, player]);

  useEffect(() => {
    try { player.muted = muted; } catch {}
  }, [muted, player]);

  // ---------------------------------------------------------------------------
  // PROJECT CRUD
  // ---------------------------------------------------------------------------

  const resetEditor = useCallback(() => {
    setSelectedProject(null);
    setVideoTitle('');
    setVideoDescription('');
    setSelectedTemplate(null);
    setSelectedClips([]);
    setTextOverlays([]);
    setSelectedAudio(null);
    setSelectedSfx([]);
    setSelectedFilter('none');
    setBrightness(100); setContrast(100); setSaturation(100);
    setPlaybackSpeed(1.0); setCurrentTime(0); setIsPlaying(false);
    setAspectRatio('9:16'); setCaption(''); setHashtags('');
    setDirty(false);
  }, []);

  const handleNewProject = useCallback(() => {
    resetEditor();
    setActiveTab('Editor');
  }, [resetEditor]);

  const handleOpenProject = useCallback((project) => {
    const d = project.raw || {};
    setSelectedProject(project);
    setVideoTitle(project.title);
    setVideoDescription(project.description || '');
    setSelectedTemplate(TEMPLATES.find((t) => t.id === d.template) || null);
    setSelectedClips(Array.isArray(d.clips) ? d.clips : []);
    setTextOverlays(Array.isArray(d.textOverlays) ? d.textOverlays : []);
    setSelectedAudio(d.audio ? AUDIO_TRACKS.find((t) => t.id === d.audio.id) || null : null);
    setSelectedSfx(Array.isArray(d.sfx) ? d.sfx : []);
    setSelectedFilter(d.filter || 'none');
    setBrightness(d.brightness ?? 100);
    setContrast(d.contrast ?? 100);
    setSaturation(d.saturation ?? 100);
    setPlaybackSpeed(d.playbackSpeed ?? 1.0);
    setAspectRatio(d.aspectRatio || '9:16');
    setExportQuality(d.exportQuality || '1080p');
    setExportFormat(d.exportFormat || 'mp4');
    setMusicVolume(d.musicVolume ?? 80);
    setOriginalVolume(d.originalVolume ?? 100);
    setFadeIn(d.fadeIn ?? true);
    setFadeOut(d.fadeOut ?? true);
    setWatermark(d.watermark ?? true);
    setCaption(d.caption || '');
    setHashtags(d.hashtags || '');
    setCurrentTime(0); setIsPlaying(false); setDirty(false);
    setActiveTab('Editor');
  }, []);

  const buildProjectData = useCallback((status = 'draft') => ({
    title: videoTitle,
    description: videoDescription,
    template: selectedTemplate?.id || null,
    aspectRatio,
    clips: selectedClips.map((c) => ({
      id: c.id, title: c.title, type: c.type, duration: c.duration,
      uri: c.uri || null, thumbnail: c.thumbnail || c.uri || null,
      trimStart: c.trimStart || 0, trimEnd: c.trimEnd || null,
      transition: c.transition || defaultTransition,
    })),
    duration: secToDur(totalDuration(selectedClips)),
    filter: selectedFilter,
    brightness, contrast, saturation, playbackSpeed,
    transition: defaultTransition,
    textOverlays,
    audio: selectedAudio ? { id: selectedAudio.id, name: selectedAudio.name } : null,
    sfx: selectedSfx,
    musicVolume, originalVolume, fadeIn, fadeOut,
    exportQuality, exportFormat, watermark,
    caption, hashtags, status,
  }), [videoTitle, videoDescription, selectedTemplate, aspectRatio, selectedClips, defaultTransition,
      selectedFilter, brightness, contrast, saturation, playbackSpeed, textOverlays, selectedAudio,
      selectedSfx, musicVolume, originalVolume, fadeIn, fadeOut, exportQuality, exportFormat,
      watermark, caption, hashtags]);

  const persistProject = useCallback(async (status = 'draft') => {
    const projectData = buildProjectData(status);
    const payload = {
      company_id: businessId,
      campaign_id: campaignId || null,
      type: 'video_project',
      name: videoTitle || 'Untitled Video',
      status,
      data: JSON.stringify(projectData),
    };
    let savedToAPI = false;
    let savedId = selectedProject?.id || null;
    try {
      const h = await getH();
      const isUpdate = savedId && !String(savedId).startsWith('vid_');
      const url = isUpdate
        ? `${API_BASE}/api/data/design_assets/${savedId}`
        : `${API_BASE}/api/data/design_assets`;
      const res = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: h,
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (res.ok || json?.success) {
        savedToAPI = true;
        savedId = json?.data?.id || json?.id || savedId;
      }
    } catch (e) { console.warn('API save failed:', e.message); }

    // Local fallback copy (always)
    try {
      const key = `video_projects_${businessId}`;
      const existing = JSON.parse((await AsyncStorage.getItem(key).catch(() => '[]')) || '[]');
      const localEntry = {
        ...payload,
        id: savedId || `vid_${Date.now()}`,
        created_at: new Date().toISOString(),
      };
      const filtered = existing.filter((p) => p.id !== localEntry.id);
      filtered.unshift(localEntry);
      await AsyncStorage.setItem(key, JSON.stringify(filtered.slice(0, 50)));
    } catch {}

    setDirty(false);
    return { savedToAPI, savedId };
  }, [buildProjectData, businessId, campaignId, videoTitle, selectedProject]);

  const handleSaveDraft = useCallback(async () => {
    if (!videoTitle.trim()) { Alert.alert('Title needed', 'Enter a project title first.'); return; }
    if (!businessId) { Alert.alert('No company', 'Company context missing — sign in again.'); return; }
    const { savedToAPI } = await persistProject('draft');
    loadProjects(false);
    Alert.alert('Saved', savedToAPI ? 'Project saved to server.' : 'Project saved locally (server unavailable).');
  }, [videoTitle, businessId, persistProject, loadProjects]);

  const handleRename = useCallback((project) => {
    setRenameProject(project);
    setRenameText(project.title);
    setRenameModal(true);
  }, []);

  const confirmRename = useCallback(async () => {
    if (!renameText.trim() || !renameProject) { setRenameModal(false); return; }
    try {
      if (!String(renameProject.id).startsWith('vid_')) {
        const h = await getH();
        await fetch(`${API_BASE}/api/data/design_assets/${renameProject.id}`, {
          method: 'PUT', headers: h,
          body: JSON.stringify({ name: renameText.trim() }),
        }).catch(() => {});
      }
      setProjects((prev) => prev.map((p) => (p.id === renameProject.id ? { ...p, title: renameText.trim() } : p)));
    } finally {
      setRenameModal(false);
      setRenameProject(null);
    }
  }, [renameText, renameProject]);

  const handleDuplicateProject = useCallback(async (project) => {
    try {
      const h = await getH();
      const d = project.raw || {};
      await fetch(`${API_BASE}/api/data/design_assets`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          company_id: businessId, campaign_id: project.campaign_id || null,
          type: 'video_project', name: `${project.title} (Copy)`, status: 'draft',
          data: JSON.stringify({ ...d, title: `${project.title} (Copy)`, status: 'draft' }),
        }),
      });
      loadProjects(false);
    } catch { Alert.alert('Duplicate failed', 'Could not duplicate project.'); }
  }, [businessId, loadProjects]);

  const handleDeleteProject = useCallback((project) => {
    Alert.alert('Delete project', `Delete "${project.title}"? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive',
        onPress: async () => {
          try {
            if (!String(project.id).startsWith('vid_')) {
              const h = await getH();
              await fetch(`${API_BASE}/api/data/design_assets/${project.id}`, { method: 'DELETE', headers: h }).catch(() => {});
            }
            const key = `video_projects_${businessId}`;
            const existing = JSON.parse((await AsyncStorage.getItem(key).catch(() => '[]')) || '[]');
            await AsyncStorage.setItem(key, JSON.stringify(existing.filter((p) => p.id !== project.id)));
            setProjects((prev) => prev.filter((p) => p.id !== project.id));
          } catch { Alert.alert('Delete failed', 'Could not delete project.'); }
        },
      },
    ]);
  }, [businessId]);

  // ---------------------------------------------------------------------------
  // MEDIA
  // ---------------------------------------------------------------------------

  const allMedia = useMemo(() => [...uploadedClips, ...STOCK_CLIPS], [uploadedClips]);

  const filteredMedia = useMemo(() => allMedia.filter((c) =>
    mediaFilter === 'All' ||
    (mediaFilter === 'Videos' && c.type === 'video') ||
    (mediaFilter === 'Images' && c.type === 'image')
  ), [allMedia, mediaFilter]);

  const handleToggleClip = useCallback((clip) => {
    setDirty(true);
    setSelectedClips((prev) =>
      prev.find((c) => c.id === clip.id)
        ? prev.filter((c) => c.id !== clip.id)
        : [...prev, { ...clip, transition: defaultTransition }]
    );
  }, [defaultTransition]);

  const handleUploadMedia = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') { Alert.alert('Permission required', 'Please allow photo library access.'); return; }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        allowsMultipleSelection: true,
        quality: 0.8,
      });
      if (!result.canceled && result.assets?.length) {
        const newClips = result.assets.map((asset, i) => ({
          id: `upload_${Date.now()}_${i}`,
          type: asset.type === 'video' ? 'video' : 'image',
          thumbnail: asset.uri,
          uri: asset.uri,
          duration: asset.duration
            ? secToDur(Math.floor(asset.duration / 1000) || Math.floor(asset.duration))
            : '0:05',
          date: new Date().toISOString(),
          title: asset.fileName || `Upload ${i + 1}`,
          width: asset.width, height: asset.height,
        }));
        setUploadedClips((prev) => [...newClips, ...prev]);
        setSelectedClips((prev) => [...prev, ...newClips.map((c) => ({ ...c, transition: defaultTransition }))]);
        setDirty(true);
        Alert.alert('Added', `${newClips.length} file${newClips.length > 1 ? 's' : ''} added to timeline.`);
      }
    } catch (e) { Alert.alert('Error', 'Failed to pick media: ' + e.message); }
  }, [defaultTransition]);

  const handleTakePhoto = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') { Alert.alert('Permission required', 'Please allow camera access.'); return; }
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        quality: 0.8,
        videoMaxDuration: 60,
      });
      if (!result.canceled && result.assets?.length) {
        const asset = result.assets[0];
        const clip = {
          id: `cam_${Date.now()}`,
          type: asset.type === 'video' ? 'video' : 'image',
          thumbnail: asset.uri, uri: asset.uri,
          duration: asset.duration ? secToDur(Math.floor(asset.duration / 1000) || Math.floor(asset.duration)) : '0:05',
          date: new Date().toISOString(),
          title: 'Camera ' + (asset.type === 'video' ? 'Video' : 'Photo'),
          transition: defaultTransition,
        };
        setUploadedClips((prev) => [clip, ...prev]);
        setSelectedClips((prev) => [...prev, clip]);
        setDirty(true);
      }
    } catch (e) { Alert.alert('Error', 'Camera error: ' + e.message); }
  }, [defaultTransition]);

  // ---------------------------------------------------------------------------
  // TIMELINE OPS
  // ---------------------------------------------------------------------------

  const moveClip = useCallback((index, dir) => {
    setSelectedClips((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  }, []);

  const removeClipAt = useCallback((index) => {
    setSelectedClips((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }, []);

  const duplicateClipAt = useCallback((index) => {
    setSelectedClips((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, { ...prev[index], id: `${prev[index].id}_d${Date.now()}` });
      return next;
    });
    setDirty(true);
  }, []);

  const openTrim = useCallback((clip) => {
    setTrimClip(clip);
    setTrimStart(String(clip.trimStart || 0));
    setTrimEnd(String(clip.trimEnd || durToSec(clip.duration)));
    setTrimModal(true);
  }, []);

  const applyTrim = useCallback(() => {
    if (!trimClip) return;
    const max = durToSec(trimClip.duration);
    const s = Math.max(0, Math.min(max - 1, parseFloat(trimStart) || 0));
    const e = Math.max(s + 1, Math.min(max, parseFloat(trimEnd) || max));
    setSelectedClips((prev) => prev.map((c) => (c.id === trimClip.id ? { ...c, trimStart: s, trimEnd: e } : c)));
    setTrimModal(false);
    setDirty(true);
  }, [trimClip, trimStart, trimEnd]);

  const openTransition = useCallback((clip) => {
    setTransitionClip(clip);
    setTransitionModal(true);
  }, []);

  const applyTransition = useCallback((t) => {
    if (transitionClip) {
      setSelectedClips((prev) => prev.map((c) => (c.id === transitionClip.id ? { ...c, transition: t } : c)));
      setDirty(true);
    }
    setTransitionModal(false);
  }, [transitionClip]);

  // ---------------------------------------------------------------------------
  // TEXT OVERLAYS
  // ---------------------------------------------------------------------------

  const openOverlayEditor = useCallback((overlay) => {
    setOverlayForm(
      overlay || {
        id: `txt_${Date.now()}`, text: '', position: 'bottom', size: 24,
        color: '#FFFFFF', animation: 'Fade In', start: 0, duration: 3,
      }
    );
    setOverlayModal(true);
  }, []);

  const saveOverlay = useCallback(() => {
    if (!overlayForm?.text?.trim()) { Alert.alert('Text needed', 'Enter the overlay text.'); return; }
    setTextOverlays((prev) => {
      const exists = prev.find((o) => o.id === overlayForm.id);
      return exists ? prev.map((o) => (o.id === overlayForm.id ? overlayForm : o)) : [...prev, overlayForm];
    });
    setOverlayModal(false);
    setDirty(true);
  }, [overlayForm]);

  const deleteOverlay = useCallback((id) => {
    setTextOverlays((prev) => prev.filter((o) => o.id !== id));
    setDirty(true);
  }, []);

  // ---------------------------------------------------------------------------
  // AI
  // ---------------------------------------------------------------------------

  const aiGenerateScript = useCallback(async () => {
    setAiWriting(true);
    try {
      const prompt =
        `Write a short social media video script for a ${secToDur(timelineTotal)} ${aspectRatio} video titled "${videoTitle || 'JobEng promo'}"` +
        (selectedTemplate ? ` using the "${selectedTemplate.name}" structure (${selectedTemplate.sections.join(' → ')})` : '') +
        (campaign?.name ? ` for the marketing campaign "${campaign.name}"` : '') +
        `. Company: ${companyName || 'a UK business'}. Give scene-by-scene voiceover lines and on-screen text suggestions, timed to ${selectedClips.length || 3} clips. British English, plain text, no markdown.`;
      const res = await askAI(prompt);
      setScriptText(typeof res === 'string' ? res : res?.content || 'No response.');
      setScriptModal(true);
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not generate script.');
    } finally { setAiWriting(false); }
  }, [askAI, timelineTotal, aspectRatio, videoTitle, selectedTemplate, campaign, companyName, selectedClips.length]);

  const aiGenerateCaption = useCallback(async () => {
    setAiWriting(true);
    try {
      const prompt =
        `Write a social media caption (max 60 words, British English, plain text) and then on a new line starting with "HASHTAGS:" give 8 relevant UK hashtags, for a video titled "${videoTitle || 'JobEng promo'}"` +
        (videoDescription ? ` about: ${videoDescription}` : '') +
        (campaign?.name ? ` (campaign: ${campaign.name})` : '') +
        `. Company: ${companyName || 'a UK business'} on the JobEng platform.`;
      const res = await askAI(prompt);
      const content = typeof res === 'string' ? res : res?.content || '';
      const parts = content.split(/HASHTAGS:/i);
      setCaption((parts[0] || '').trim());
      if (parts[1]) setHashtags(parts[1].trim());
      setDirty(true);
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not generate caption.');
    } finally { setAiWriting(false); }
  }, [askAI, videoTitle, videoDescription, campaign, companyName]);

  // ---------------------------------------------------------------------------
  // EXPORT
  // ---------------------------------------------------------------------------

  const handleExport = useCallback(async () => {
    if (!videoTitle.trim()) { Alert.alert('Title needed', 'Enter a video title.'); setActiveTab('Export'); return; }
    if (selectedClips.length === 0) { Alert.alert('No clips', 'Add at least one clip in the Editor tab.'); return; }
    if (!businessId) { Alert.alert('No company', 'Company context missing — sign in again.'); return; }

    setIsExporting(true);
    setExportProgress(0);
    // Simulated render progress (real rendering requires a dev build with native video libs)
    await new Promise((resolve) => {
      const iv = setInterval(() => {
        setExportProgress((p) => {
          const next = p + 7 + Math.random() * 8;
          if (next >= 100) { clearInterval(iv); resolve(); return 100; }
          return next;
        });
      }, 220);
    });

    try {
      const { savedToAPI } = await persistProject('completed');
      if (campaignId) {
        const key = `campaign_content_${campaignId}`;
        const arr = JSON.parse((await AsyncStorage.getItem(key).catch(() => '[]')) || '[]');
        arr.unshift({
          type: 'video', title: videoTitle, status: 'exported',
          quality: exportQuality, format: exportFormat,
          created_at: new Date().toISOString(), id: `vid_${Date.now()}`,
        });
        await AsyncStorage.setItem(key, JSON.stringify(arr.slice(0, 50)));
      }
      loadProjects(false);

      const firstVideo = selectedClips.find(
        (c) => c.type === 'video' && c.uri && !c.uri.startsWith('http')
      ) || selectedClips.find((c) => c.uri && !c.uri.startsWith('http'));

      const shareVideoFile = async () => {
        try {
          if (!firstVideo) {
            Alert.alert('No local file', 'Only uploaded or camera clips can be shared as files. Stock thumbnails cannot.');
            return;
          }
          const available = await Sharing.isAvailableAsync();
          if (!available) {
            Alert.alert('Sharing unavailable', 'File sharing is not available on this device.');
            return;
          }
          await Sharing.shareAsync(firstVideo.uri, {
            mimeType: firstVideo.type === 'video' ? 'video/mp4' : 'image/jpeg',
            dialogTitle: videoTitle,
          });
        } catch (e) {
          Alert.alert('Share failed', e?.message || 'Could not share the file.');
        }
      };

      Alert.alert(
        '✅ Export complete',
        `"${videoTitle}" (${exportQuality} ${exportFormat.toUpperCase()}, ${secToDur(timelineTotal)}) saved${campaignId ? ' to campaign assets' : ''}.${firstVideo ? '' : '\n\nNote: no local media to share as a file.'}`,
        [
          ...(firstVideo ? [{ text: 'Share video file', onPress: shareVideoFile }] : []),
          { text: 'Share caption', onPress: () => {
            Share.share({
              message: `🎬 ${videoTitle}\n${caption ? caption + '\n' : ''}${hashtags ? hashtags + '\n' : ''}${secToDur(timelineTotal)} · ${exportQuality} · ${selectedClips.length} clips`,
            }).catch(() => {});
          }},
          { text: 'Done', onPress: () => setActiveTab('Projects') },
        ]
      );
    } catch {
      Alert.alert('Export saved locally', 'Server unavailable — project kept on this device.');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  }, [videoTitle, selectedClips, businessId, persistProject, campaignId, exportQuality, exportFormat, timelineTotal, caption, hashtags, loadProjects]);

  // ---------------------------------------------------------------------------
  // DERIVED
  // ---------------------------------------------------------------------------

  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => p.title.toLowerCase().includes(q));
  }, [projects, projectSearch]);

  const ratio = ASPECT_RATIOS.find((r) => r.key === aspectRatio) || ASPECT_RATIOS[0];
  const previewW = Math.min(SCREEN_W - 64, 280);
  const previewH = Math.min(320, (previewW * ratio.h) / ratio.w);
  const filterDef = FILTERS.find((f) => f.id === selectedFilter) || FILTERS[0];
  const currentClip = selectedClips[currentClipIndex];

  // ---------------------------------------------------------------------------
  // PREVIEW WINDOW (shared by Studio & Text tabs)
  // ---------------------------------------------------------------------------

  const renderPreview = () => (
    <View style={[st.previewWrap, { height: previewH + 60 }]}>
      <View style={[st.previewBox, { width: (previewH * ratio.w) / ratio.h, height: previewH }]}>
        {videoSource ? (
          <VideoView
            player={player}
            style={{ width: '100%', height: '100%' }}
            contentFit="cover"
            nativeControls={false}
          />
        ) : currentClip?.uri || currentClip?.thumbnail ? (
          <Image
            source={{ uri: currentClip.uri || currentClip.thumbnail }}
            style={{ width: '100%', height: '100%' }}
            resizeMode="cover"
            onError={() => {}}
          />
        ) : (
          <View style={st.previewEmpty}>
            <MaterialCommunityIcons name="movie-open-outline" size={34} color="#475569" />
            <Text style={{ color: '#64748B', fontSize: 11, marginTop: 6 }}>Add clips in Editor</Text>
          </View>
        )}
        {/* Filter tint */}
        {filterDef.op > 0 && (
          <View style={[StyleSheet.absoluteFill, { backgroundColor: filterDef.tint, opacity: filterDef.op }]} pointerEvents="none" />
        )}
        {/* Brightness sim */}
        {brightness !== 100 && (
          <View
            style={[StyleSheet.absoluteFill, {
              backgroundColor: brightness > 100 ? '#fff' : '#000',
              opacity: Math.min(0.6, Math.abs(brightness - 100) / 150),
            }]}
            pointerEvents="none"
          />
        )}
        {/* Watermark */}
        {watermark && (
          <View style={st.watermark}>
            <CompanyLogo uri={companyLogo} size={18} />
            <Text style={st.watermarkText}>{companyName || 'JobEng'}</Text>
          </View>
        )}
        {/* Active text overlays */}
        {activeOverlays.map((o) => (
          <View
            key={o.id}
            pointerEvents="none"
            style={[st.overlayLayer, o.position === 'top' && { top: 12 }, o.position === 'middle' && { top: '45%' }, o.position === 'bottom' && { bottom: 12 }]}
          >
            <Text style={{ color: o.color, fontSize: Math.min(o.size, 28), fontWeight: '800', textAlign: 'center', textShadowColor: 'rgba(0,0,0,0.6)', textShadowRadius: 4 }}>
              {o.text}
            </Text>
          </View>
        ))}
        {/* Clip counter */}
        {selectedClips.length > 0 && (
          <View style={st.clipCounter}>
            <Text style={st.clipCounterText}>
              Clip {currentClipIndex + 1}/{selectedClips.length}
            </Text>
          </View>
        )}
      </View>

      {/* Transport */}
      <View style={st.transport}>
        <TouchableOpacity style={st.transportBtn} onPress={() => { setCurrentTime(0); }}>
          <Ionicons name="play-skip-back" size={16} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity
          style={[st.transportBtn, st.transportMain]}
          onPress={() => {
            if (selectedClips.length === 0) { Alert.alert('No clips', 'Add clips in the Editor tab first.'); return; }
            setIsPlaying((p) => !p);
          }}
        >
          <Ionicons name={isPlaying ? 'pause' : 'play'} size={20} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={st.transportBtn} onPress={() => setCurrentTime(Math.min(timelineTotal - 0.1, currentTime + 5))}>
          <Ionicons name="play-skip-forward" size={16} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={st.transportBtn} onPress={() => setPlaybackSpeed((s) => (s >= 2 ? 0.5 : s + 0.25))}>
          <Text style={{ color: '#fff', fontSize: 10, fontWeight: '900' }}>{playbackSpeed}x</Text>
        </TouchableOpacity>
        <TouchableOpacity style={st.transportBtn} onPress={() => setMuted((m) => !m)}>
          <Ionicons name={muted ? 'volume-mute' : 'volume-high'} size={15} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Scrub bar */}
      <View style={st.scrubRow}>
        <Text style={st.scrubTime}>{secToDur(currentTime)}</Text>
        <TouchableOpacity
          style={st.scrubTrack}
          activeOpacity={0.9}
          onPress={(e) => {
            const x = e.nativeEvent.locationX;
            const trackW = SCREEN_W - 140;
            setCurrentTime(Math.max(0, Math.min(timelineTotal, (x / trackW) * timelineTotal)));
          }}
        >
          <View style={[st.scrubFill, { width: `${Math.min(100, (currentTime / timelineTotal) * 100)}%` }]} />
        </TouchableOpacity>
        <Text style={st.scrubTime}>{secToDur(timelineTotal)}</Text>
      </View>
    </View>
  );

  // ---------------------------------------------------------------------------
  // TABS
  // ---------------------------------------------------------------------------

  const renderProjects = () => (
    <ScrollView
      style={st.tabContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadProjects(false); }} tintColor={C.primary} />}
    >
      <SectionTitle
        right={
          <TouchableOpacity style={st.newBtn} onPress={handleNewProject}>
            <MaterialCommunityIcons name="plus" size={16} color="#fff" />
            <Text style={st.newBtnText}>New Project</Text>
          </TouchableOpacity>
        }
      >
        Your Video Projects ({projects.length})
      </SectionTitle>

      <View style={st.searchWrap}>
        <Ionicons name="search" size={15} color={C.sub} />
        <TextInput
          style={st.searchInput}
          value={projectSearch}
          onChangeText={setProjectSearch}
          placeholder="Search projects…"
          placeholderTextColor="#9CA3AF"
        />
      </View>

      {loadingProjects ? (
        <ActivityIndicator size="large" color={C.primary} style={{ marginTop: 40 }} />
      ) : filteredProjects.length === 0 ? (
        <View style={st.empty}>
          <MaterialCommunityIcons name="movie-open-plus-outline" size={48} color="#CBD5E1" />
          <Text style={st.emptyTitle}>
            {businessId === 0 ? 'No company context' : 'No video projects yet'}
          </Text>
          <Text style={st.emptyText}>
            {businessId === 0
              ? 'Sign in to your business account to create videos.'
              : 'Create marketing videos for your campaigns, job adverts, and social media.'}
          </Text>
        </View>
      ) : (
        filteredProjects.map((p) => (
          <ProjectCard
            key={String(p.id)}
            project={p}
            onOpen={handleOpenProject}
            onRename={handleRename}
            onDuplicate={handleDuplicateProject}
            onDelete={handleDeleteProject}
          />
        ))
      )}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderEditor = () => (
    <ScrollView style={st.tabContent} keyboardShouldPersistTaps="handled">
      {campaign?.name && (
        <View style={st.campaignBanner}>
          <MaterialCommunityIcons name="bullhorn-outline" size={14} color={C.primary} />
          <Text style={st.campaignBannerText}>Campaign: {campaign.name}</Text>
        </View>
      )}

      <SectionTitle>Project Details</SectionTitle>
      <TextInput
        style={st.input}
        value={videoTitle}
        onChangeText={(t) => { setVideoTitle(t); setDirty(true); }}
        placeholder="Video title *"
        placeholderTextColor="#9CA3AF"
      />
      <TextInput
        style={[st.input, { minHeight: 64, textAlignVertical: 'top' }]}
        value={videoDescription}
        onChangeText={(t) => { setVideoDescription(t); setDirty(true); }}
        placeholder="Description (optional)"
        placeholderTextColor="#9CA3AF"
        multiline
      />

      <SectionTitle>Aspect Ratio</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.chipScroll}>
        {ASPECT_RATIOS.map((r) => (
          <Chip key={r.key} label={`${r.key} ${r.label}`} active={aspectRatio === r.key} onPress={() => { setAspectRatio(r.key); setDirty(true); }} />
        ))}
      </ScrollView>

      <SectionTitle>Templates</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.chipScroll}>
        {TEMPLATES.map((t) => (
          <TemplateCard key={t.id} template={t} active={selectedTemplate?.id === t.id} onPress={(tpl) => { setSelectedTemplate(selectedTemplate?.id === tpl.id ? null : tpl); setDirty(true); }} />
        ))}
      </ScrollView>
      {selectedTemplate && (
        <View style={st.templateSections}>
          {selectedTemplate.sections.map((s, i) => (
            <View key={s} style={st.templateSectionRow}>
              <Text style={st.templateSectionNum}>{i + 1}</Text>
              <Text style={st.templateSectionText}>{s}</Text>
            </View>
          ))}
        </View>
      )}

      <SectionTitle
        right={
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {['All', 'Videos', 'Images'].map((f) => (
              <Chip key={f} small label={f} active={mediaFilter === f} onPress={() => setMediaFilter(f)} />
            ))}
          </View>
        }
      >
        Media Library
      </SectionTitle>
      <View style={st.mediaGrid}>
        {filteredMedia.map((clip) => (
          <MediaClipCard
            key={clip.id}
            clip={clip}
            selected={!!selectedClips.find((c) => c.id === clip.id)}
            onPress={handleToggleClip}
          />
        ))}
      </View>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <TouchableOpacity style={[st.bigBtn, { flex: 1 }]} onPress={handleUploadMedia}>
          <MaterialCommunityIcons name="upload" size={17} color="#fff" />
          <Text style={st.bigBtnText}>Upload Media</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[st.bigBtn, { flex: 1, backgroundColor: C.success }]} onPress={handleTakePhoto}>
          <Ionicons name="camera" size={17} color="#fff" />
          <Text style={st.bigBtnText}>Camera</Text>
        </TouchableOpacity>
      </View>

      {selectedClips.length > 0 && (
        <>
          <SectionTitle
            right={
              <TouchableOpacity
                onPress={() => Alert.alert('Clear timeline', 'Remove all clips?', [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Clear', style: 'destructive', onPress: () => { setSelectedClips([]); setDirty(true); } },
                ])}
              >
                <Text style={{ fontSize: 11, fontWeight: '700', color: C.danger }}>Clear All</Text>
              </TouchableOpacity>
            }
          >
            Timeline · {selectedClips.length} clips · {secToDur(timelineTotal)}
          </SectionTitle>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingVertical: 4, gap: 8 }}>
            {selectedClips.map((clip, i) => (
              <TimelineClip
                key={`tl-${clip.id}-${i}`}
                clip={clip}
                index={i}
                total={selectedClips.length}
                onMoveLeft={(idx) => moveClip(idx, -1)}
                onMoveRight={(idx) => moveClip(idx, 1)}
                onTrim={openTrim}
                onTransition={openTransition}
                onDuplicate={duplicateClipAt}
                onRemove={removeClipAt}
              />
            ))}
          </ScrollView>
        </>
      )}

      <TouchableOpacity style={[st.bigBtn, { backgroundColor: C.primary, marginTop: 16 }]} onPress={handleSaveDraft}>
        <Ionicons name="save-outline" size={17} color="#fff" />
        <Text style={st.bigBtnText}>Save Draft{dirty ? ' •' : ''}</Text>
      </TouchableOpacity>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderStudio = () => (
    <ScrollView style={st.tabContent}>
      {renderPreview()}

      <SectionTitle>Filters</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.chipScroll}>
        {FILTERS.map((f) => (
          <TouchableOpacity key={f.id} style={[st.filterCard, selectedFilter === f.id && st.filterCardActive]} onPress={() => { setSelectedFilter(f.id); setDirty(true); }}>
            <View style={[st.filterSwatch, { backgroundColor: f.tint === 'transparent' ? '#E2E8F0' : f.tint }]} />
            <Text style={[st.filterName, selectedFilter === f.id && { color: '#fff' }]}>{f.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <SectionTitle>Colour Adjustments</SectionTitle>
      <View style={st.panel}>
        <Stepper label="Brightness" value={brightness} onChange={(v) => { setBrightness(v); setDirty(true); }} />
        <Stepper label="Contrast" value={contrast} onChange={(v) => { setContrast(v); setDirty(true); }} />
        <Stepper label="Saturation" value={saturation} onChange={(v) => { setSaturation(v); setDirty(true); }} />
        <TouchableOpacity onPress={() => { setBrightness(100); setContrast(100); setSaturation(100); setSelectedFilter('none'); }}>
          <Text style={{ fontSize: 12, fontWeight: '700', color: C.primary, textAlign: 'center', marginTop: 4 }}>Reset all</Text>
        </TouchableOpacity>
      </View>

      <SectionTitle>Default Transition</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.chipScroll}>
        {TRANSITIONS.map((t) => (
          <Chip key={t} label={t} active={defaultTransition === t} onPress={() => { setDefaultTransition(t); setDirty(true); }} />
        ))}
      </ScrollView>
      <Text style={st.hint}>Per-clip transitions: tap the ⇄ icon on a timeline clip in the Editor tab.</Text>

      <SectionTitle>Playback Speed</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.chipScroll}>
        {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map((s) => (
          <Chip key={s} label={`${s}x`} active={playbackSpeed === s} onPress={() => { setPlaybackSpeed(s); setDirty(true); }} />
        ))}
      </ScrollView>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderText = () => (
    <ScrollView style={st.tabContent}>
      {renderPreview()}
      <SectionTitle
        right={
          <TouchableOpacity style={st.newBtn} onPress={() => openOverlayEditor(null)}>
            <MaterialCommunityIcons name="plus" size={15} color="#fff" />
            <Text style={st.newBtnText}>Add Text</Text>
          </TouchableOpacity>
        }
      >
        Text Overlays ({textOverlays.length})
      </SectionTitle>
      {textOverlays.length === 0 ? (
        <View style={st.empty}>
          <MaterialCommunityIcons name="format-text" size={40} color="#CBD5E1" />
          <Text style={st.emptyText}>Add titles, captions, and calls to action over your video.</Text>
        </View>
      ) : (
        textOverlays
          .slice()
          .sort((a, b) => a.start - b.start)
          .map((o) => <OverlayCard key={o.id} overlay={o} onEdit={openOverlayEditor} onDelete={deleteOverlay} />)
      )}
      <Text style={st.hint}>Overlays appear in the preview while playback passes their time window.</Text>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderAudio = () => (
    <ScrollView style={st.tabContent}>
      <SectionTitle>Music Library</SectionTitle>
      {AUDIO_TRACKS.map((t) => (
        <TrackCard
          key={t.id}
          track={t}
          active={selectedAudio?.id === t.id}
          onPress={(track) => { setSelectedAudio(selectedAudio?.id === track.id ? null : track); setDirty(true); }}
        />
      ))}

      <SectionTitle>Sound Effects</SectionTitle>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {SFX.map((s) => (
          <Chip
            key={s}
            small
            label={selectedSfx.includes(s) ? `✓ ${s}` : s}
            active={selectedSfx.includes(s)}
            onPress={() => { setSelectedSfx((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])); setDirty(true); }}
          />
        ))}
      </View>

      <SectionTitle>Mixer</SectionTitle>
      <View style={st.panel}>
        <Stepper label="Music volume" value={musicVolume} onChange={(v) => { setMusicVolume(v); setDirty(true); }} max={100} />
        <Stepper label="Original audio" value={originalVolume} onChange={(v) => { setOriginalVolume(v); setDirty(true); }} max={100} />
        <Toggle label="Fade in" value={fadeIn} onChange={(v) => { setFadeIn(v); setDirty(true); }} />
        <Toggle label="Fade out" value={fadeOut} onChange={(v) => { setFadeOut(v); setDirty(true); }} />
      </View>
      <Text style={st.hint}>Voiceover recording requires a development build (native audio module) — coming with the next dev client.</Text>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderExport = () => (
    <ScrollView style={st.tabContent} keyboardShouldPersistTaps="handled">
      <SectionTitle>AI Content</SectionTitle>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <TouchableOpacity style={[st.aiBtn, { flex: 1 }]} onPress={aiGenerateScript} disabled={aiWriting}>
          {aiWriting ? <ActivityIndicator size="small" color={C.accent} /> : <MaterialCommunityIcons name="script-text-outline" size={16} color={C.accent} />}
          <Text style={st.aiBtnText}>AI Script</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[st.aiBtn, { flex: 1 }]} onPress={aiGenerateCaption} disabled={aiWriting}>
          {aiWriting ? <ActivityIndicator size="small" color={C.accent} /> : <MaterialCommunityIcons name="brain" size={16} color={C.accent} />}
          <Text style={st.aiBtnText}>AI Caption + Hashtags</Text>
        </TouchableOpacity>
      </View>

      <SectionTitle>Caption & Hashtags</SectionTitle>
      <TextInput
        style={[st.input, { minHeight: 70, textAlignVertical: 'top' }]}
        value={caption}
        onChangeText={(t) => { setCaption(t); setDirty(true); }}
        placeholder="Post caption (used when sharing)"
        placeholderTextColor="#9CA3AF"
        multiline
      />
      <TextInput
        style={st.input}
        value={hashtags}
        onChangeText={(t) => { setHashtags(t); setDirty(true); }}
        placeholder="#hashtags"
        placeholderTextColor="#9CA3AF"
      />

      <SectionTitle>Quality</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.chipScroll}>
        {EXPORT_QUALITIES.map((q) => (
          <Chip key={q} label={q} active={exportQuality === q} onPress={() => { setExportQuality(q); setDirty(true); }} />
        ))}
      </ScrollView>

      <SectionTitle>Format</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={st.chipScroll}>
        {EXPORT_FORMATS.map((f) => (
          <Chip key={f} label={f.toUpperCase()} active={exportFormat === f} onPress={() => { setExportFormat(f); setDirty(true); }} />
        ))}
      </ScrollView>

      <View style={st.panel}>
        <Toggle label={`Watermark (${companyName || 'company'} logo)`} value={watermark} onChange={(v) => { setWatermark(v); setDirty(true); }} />
      </View>

      <SectionTitle>Summary</SectionTitle>
      <View style={st.summary}>
        {[
          { l: 'Title', v: videoTitle || 'Untitled' },
          { l: 'Clips', v: String(selectedClips.length) },
          { l: 'Duration', v: secToDur(timelineTotal) },
          { l: 'Aspect', v: aspectRatio },
          { l: 'Template', v: selectedTemplate?.name || 'None' },
          { l: 'Filter', v: filterDef.name },
          { l: 'Audio', v: selectedAudio?.name || 'None' },
          { l: 'Text overlays', v: String(textOverlays.length) },
          { l: 'Output', v: `${exportQuality} ${exportFormat.toUpperCase()}` },
          { l: 'Campaign', v: campaign?.name || (campaignId ? `#${campaignId}` : 'None') },
        ].map((r) => (
          <View key={r.l} style={st.summaryRow}>
            <Text style={st.summaryLabel}>{r.l}</Text>
            <Text style={st.summaryValue} numberOfLines={1}>{r.v}</Text>
          </View>
        ))}
      </View>

      {isExporting ? (
        <View style={st.exportProgressWrap}>
          <Text style={st.exportProgressText}>Rendering… {Math.floor(exportProgress)}%</Text>
          <View style={st.scrubTrack}>
            <View style={[st.scrubFill, { width: `${exportProgress}%` }]} />
          </View>
        </View>
      ) : (
        <TouchableOpacity
          style={[st.bigBtn, { backgroundColor: C.success, marginTop: 14, opacity: selectedClips.length === 0 || !videoTitle.trim() ? 0.5 : 1 }]}
          onPress={handleExport}
          disabled={selectedClips.length === 0 || !videoTitle.trim()}
        >
          <MaterialCommunityIcons name="export-variant" size={18} color="#fff" />
          <Text style={st.bigBtnText}>Export Video</Text>
        </TouchableOpacity>
      )}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'Projects': return renderProjects();
      case 'Editor': return renderEditor();
      case 'Studio': return renderStudio();
      case 'Text': return renderText();
      case 'Audio': return renderAudio();
      case 'Export': return renderExport();
      default: return renderProjects();
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <SafeAreaView style={st.safe} edges={['top']}>
      {/* Header */}
      <View style={st.header}>
        <TouchableOpacity style={st.headerBtn} onPress={() => navigation?.goBack?.()}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <CompanyLogo uri={companyLogo} size={28} />
        <View style={{ flex: 1, marginLeft: 10 }}>
          <Text style={st.headerTitle}>Video Editor</Text>
          <Text style={st.headerSub} numberOfLines={1}>
            {videoTitle || companyName || 'New project'}{dirty ? ' · unsaved' : ''}
          </Text>
        </View>
        <TouchableOpacity style={st.headerBtn} onPress={() => setShowAIChat(true)}>
          <MaterialCommunityIcons name="brain" size={22} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Tab bar */}
      <View style={st.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {TABS.map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[st.tabBtn, activeTab === tab && st.tabBtnActive]}
              onPress={() => setActiveTab(tab)}
            >
              <Text style={[st.tabText, activeTab === tab && st.tabTextActive]}>{tab}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {renderTab()}

      {/* ============ TRIM MODAL ============ */}
      <Modal visible={trimModal} transparent animationType="fade" onRequestClose={() => setTrimModal(false)}>
        <View style={st.modalOverlay}>
          <View style={st.modalBox}>
            <Text style={st.modalTitle}>Trim clip</Text>
            <Text style={st.modalSub}>{trimClip?.title} · max {trimClip ? secToDur(durToSec(trimClip.duration)) : ''}</Text>
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={st.fieldLabel}>Start (sec)</Text>
                <TextInput style={st.input} value={trimStart} onChangeText={setTrimStart} keyboardType="numeric" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={st.fieldLabel}>End (sec)</Text>
                <TextInput style={st.input} value={trimEnd} onChangeText={setTrimEnd} keyboardType="numeric" />
              </View>
            </View>
            <View style={st.modalBtnRow}>
              <TouchableOpacity style={st.modalCancel} onPress={() => setTrimModal(false)}>
                <Text style={st.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={st.modalOk} onPress={applyTrim}>
                <Text style={st.modalOkText}>Apply Trim</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ============ TRANSITION MODAL ============ */}
      <Modal visible={transitionModal} transparent animationType="fade" onRequestClose={() => setTransitionModal(false)}>
        <View style={st.modalOverlay}>
          <View style={st.modalBox}>
            <Text style={st.modalTitle}>Transition after "{transitionClip?.title}"</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
              {TRANSITIONS.map((t) => (
                <Chip key={t} label={t} active={transitionClip?.transition === t} onPress={() => applyTransition(t)} />
              ))}
            </View>
            <TouchableOpacity style={[st.modalCancel, { marginTop: 16 }]} onPress={() => setTransitionModal(false)}>
              <Text style={st.modalCancelText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* ============ TEXT OVERLAY MODAL ============ */}
      <Modal visible={overlayModal} transparent animationType="slide" onRequestClose={() => setOverlayModal(false)}>
        <View style={st.modalOverlay}>
          <View style={st.modalBox}>
            <Text style={st.modalTitle}>{overlayForm && textOverlays.find((o) => o.id === overlayForm.id) ? 'Edit text' : 'Add text'}</Text>
            <TextInput
              style={[st.input, { marginTop: 12 }]}
              value={overlayForm?.text || ''}
              onChangeText={(t) => setOverlayForm((p) => ({ ...p, text: t }))}
              placeholder="Your text…"
              placeholderTextColor="#9CA3AF"
            />
            <Text style={st.fieldLabel}>Position</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {TEXT_POSITIONS.map((p) => (
                <Chip key={p} small label={p} active={overlayForm?.position === p} onPress={() => setOverlayForm((f) => ({ ...f, position: p }))} />
              ))}
            </View>
            <Text style={st.fieldLabel}>Size</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {TEXT_SIZES.map((s) => (
                <Chip key={s} small label={`${s}`} active={overlayForm?.size === s} onPress={() => setOverlayForm((f) => ({ ...f, size: s }))} />
              ))}
            </View>
            <Text style={st.fieldLabel}>Colour</Text>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              {TEXT_COLORS.map((c) => (
                <TouchableOpacity
                  key={c}
                  style={[st.colorDot, { backgroundColor: c }, overlayForm?.color === c && st.colorDotActive]}
                  onPress={() => setOverlayForm((f) => ({ ...f, color: c }))}
                />
              ))}
            </View>
            <Text style={st.fieldLabel}>Animation</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {TEXT_ANIMATIONS.map((a) => (
                <Chip key={a} small label={a} active={overlayForm?.animation === a} onPress={() => setOverlayForm((f) => ({ ...f, animation: a }))} />
              ))}
            </View>
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 8 }}>
              <View style={{ flex: 1 }}>
                <Text style={st.fieldLabel}>Start (sec)</Text>
                <TextInput
                  style={st.input}
                  value={String(overlayForm?.start ?? 0)}
                  onChangeText={(t) => setOverlayForm((f) => ({ ...f, start: Math.max(0, parseFloat(t) || 0) }))}
                  keyboardType="numeric"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={st.fieldLabel}>Duration (sec)</Text>
                <TextInput
                  style={st.input}
                  value={String(overlayForm?.duration ?? 3)}
                  onChangeText={(t) => setOverlayForm((f) => ({ ...f, duration: Math.max(1, parseFloat(t) || 3) }))}
                  keyboardType="numeric"
                />
              </View>
            </View>
            <View style={st.modalBtnRow}>
              <TouchableOpacity style={st.modalCancel} onPress={() => setOverlayModal(false)}>
                <Text style={st.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={st.modalOk} onPress={saveOverlay}>
                <Text style={st.modalOkText}>Save Text</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ============ RENAME MODAL ============ */}
      <Modal visible={renameModal} transparent animationType="fade" onRequestClose={() => setRenameModal(false)}>
        <View style={st.modalOverlay}>
          <View style={st.modalBox}>
            <Text style={st.modalTitle}>Rename project</Text>
            <TextInput style={[st.input, { marginTop: 12 }]} value={renameText} onChangeText={setRenameText} placeholder="Project title" placeholderTextColor="#9CA3AF" />
            <View style={st.modalBtnRow}>
              <TouchableOpacity style={st.modalCancel} onPress={() => setRenameModal(false)}>
                <Text style={st.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={st.modalOk} onPress={confirmRename}>
                <Text style={st.modalOkText}>Rename</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ============ AI SCRIPT MODAL ============ */}
      <Modal visible={scriptModal} transparent animationType="slide" onRequestClose={() => setScriptModal(false)}>
        <View style={st.modalOverlay}>
          <View style={[st.modalBox, { maxHeight: '75%' }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={st.modalTitle}>AI Video Script</Text>
              <TouchableOpacity onPress={() => setScriptModal(false)}>
                <Ionicons name="close" size={22} color={C.text} />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ marginTop: 10 }}>
              <Text style={{ fontSize: 13, lineHeight: 20, color: C.text }}>{scriptText}</Text>
            </ScrollView>
            <TouchableOpacity
              style={[st.modalOk, { marginTop: 12 }]}
              onPress={() => { Share.share({ message: scriptText }).catch(() => {}); }}
            >
              <Text style={st.modalOkText}>Share Script</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <AIChatWidget
        screenName="VideoEditorScreen"
        visible={showAIChat}
        onClose={() => setShowAIChat(false)}
        title="Video AI"
        extraContext={{ videoTitle, clips: selectedClips.length, campaign: campaign?.name || null }}
      />
    </SafeAreaView>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 8, paddingVertical: 10 },
  headerBtn: { padding: 8 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 1 },

  tabBar: { backgroundColor: C.primary, paddingBottom: 8, paddingHorizontal: 8 },
  tabBtn: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, marginRight: 6, backgroundColor: 'rgba(255,255,255,0.12)' },
  tabBtnActive: { backgroundColor: '#fff' },
  tabText: { fontSize: 12.5, fontWeight: '700', color: 'rgba(255,255,255,0.8)' },
  tabTextActive: { color: C.primary },

  tabContent: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 14, paddingTop: 12 },

  sectionTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, marginBottom: 8 },
  sectionTitle: { fontSize: 14.5, fontWeight: '800', color: C.text },

  newBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: C.primary, paddingHorizontal: 11, paddingVertical: 7, borderRadius: 10 },
  newBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  searchWrap: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 10, paddingHorizontal: 10, borderWidth: 1, borderColor: C.border, marginBottom: 4 },
  searchInput: { flex: 1, paddingVertical: Platform.OS === 'ios' ? 9 : 7, paddingHorizontal: 8, fontSize: 13.5, color: C.text },

  empty: { alignItems: 'center', paddingVertical: 36, paddingHorizontal: 24 },
  emptyTitle: { fontSize: 15, fontWeight: '700', color: C.text, marginTop: 10 },
  emptyText: { fontSize: 12.5, color: C.sub, textAlign: 'center', marginTop: 6, lineHeight: 18 },

  projectCard: { backgroundColor: C.card, borderRadius: 14, padding: 12, marginTop: 10, borderWidth: 1, borderColor: C.border },
  projectMain: { flexDirection: 'row', alignItems: 'center' },
  projectThumb: { width: 64, height: 48, borderRadius: 10, backgroundColor: C.darkCard, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  projectTitle: { fontSize: 14, fontWeight: '700', color: C.text },
  projectMetaRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 3 },
  projectMeta: { fontSize: 11, color: C.sub },
  statusPill: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8, marginTop: 6 },
  statusPillText: { fontSize: 10, fontWeight: '700' },
  projectActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 4, marginTop: 8, borderTopWidth: 1, borderTopColor: C.border, paddingTop: 8 },
  projIconBtn: { padding: 7, borderRadius: 8 },

  campaignBanner: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#EFF4FF', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7 },
  campaignBannerText: { fontSize: 12, fontWeight: '700', color: C.primary },

  input: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: Platform.OS === 'ios' ? 10 : 8, fontSize: 13.5, color: C.text, marginBottom: 8 },
  fieldLabel: { fontSize: 11.5, fontWeight: '700', color: C.text, marginTop: 10, marginBottom: 6 },

  chipScroll: { gap: 8, paddingVertical: 2 },
  chip: { paddingHorizontal: 13, paddingVertical: 7, borderRadius: 16, backgroundColor: C.card, borderWidth: 1, borderColor: C.border },
  chipSmall: { paddingHorizontal: 10, paddingVertical: 5 },
  chipActive: { backgroundColor: C.primary, borderColor: C.primary },
  chipText: { fontSize: 12.5, fontWeight: '600', color: C.sub },
  chipTextActive: { color: '#fff' },

  templateCard: { width: 130, backgroundColor: C.card, borderRadius: 12, padding: 12, borderWidth: 1, borderColor: C.border },
  templateCardActive: { backgroundColor: C.primary, borderColor: C.primary },
  templateName: { fontSize: 12.5, fontWeight: '700', color: C.text, marginTop: 8 },
  templateDur: { fontSize: 10, color: C.sub, marginTop: 2 },
  templateSections: { backgroundColor: C.card, borderRadius: 12, padding: 12, marginTop: 8, borderWidth: 1, borderColor: C.border },
  templateSectionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 },
  templateSectionNum: { width: 18, height: 18, borderRadius: 9, backgroundColor: C.primary, color: '#fff', fontSize: 10, fontWeight: '800', textAlign: 'center', lineHeight: 18, overflow: 'hidden' },
  templateSectionText: { fontSize: 12.5, color: C.text },

  mediaGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  mediaClip: { width: (SCREEN_W - 44) / 3 },
  mediaClipSelected: {},
  mediaThumb: { height: 72, borderRadius: 10, backgroundColor: C.darkCard, overflow: 'hidden' },
  mediaPlayBadge: { position: 'absolute', top: 4, left: 4, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 8, padding: 2 },
  mediaDurBadge: { position: 'absolute', bottom: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.65)', borderRadius: 6, paddingHorizontal: 4, paddingVertical: 1 },
  mediaDurText: { fontSize: 9, color: '#fff', fontWeight: '700' },
  mediaCheck: { position: 'absolute', top: 4, right: 4, backgroundColor: '#fff', borderRadius: 10 },
  mediaTitle: { fontSize: 10.5, color: C.text, marginTop: 4, fontWeight: '600' },

  bigBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: C.primaryLight, borderRadius: 12, paddingVertical: 12, marginTop: 4 },
  bigBtnText: { color: '#fff', fontSize: 13.5, fontWeight: '800' },

  tlClip: { width: 108, backgroundColor: C.card, borderRadius: 12, padding: 8, borderWidth: 1, borderColor: C.border },
  tlThumb: { height: 56, borderRadius: 8, backgroundColor: C.darkCard, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' },
  tlIndex: { position: 'absolute', top: 3, left: 3, backgroundColor: C.primary, borderRadius: 7, width: 14, height: 14, alignItems: 'center', justifyContent: 'center' },
  tlIndexText: { fontSize: 8, color: '#fff', fontWeight: '800' },
  tlTitle: { fontSize: 9.5, fontWeight: '700', color: C.text, marginTop: 5 },
  tlDur: { fontSize: 9, color: C.sub },
  tlTransition: { fontSize: 8.5, color: C.accent, fontWeight: '700' },
  tlBtns: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 },
  tlBtn: { padding: 2 },

  previewWrap: { alignItems: 'center', backgroundColor: C.dark, borderRadius: 16, paddingTop: 12, marginTop: 4 },
  previewBox: { backgroundColor: '#000', borderRadius: 10, overflow: 'hidden' },
  previewEmpty: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  watermark: { position: 'absolute', top: 8, right: 8, flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.35)', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 3 },
  watermarkText: { fontSize: 9, color: '#fff', fontWeight: '700' },
  overlayLayer: { position: 'absolute', left: 8, right: 8, alignItems: 'center' },
  clipCounter: { position: 'absolute', bottom: 6, left: 6, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 7, paddingHorizontal: 6, paddingVertical: 2 },
  clipCounterText: { fontSize: 9, color: '#fff', fontWeight: '700' },

  transport: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 },
  transportBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.12)', alignItems: 'center', justifyContent: 'center' },
  transportMain: { width: 46, height: 46, borderRadius: 23, backgroundColor: C.primaryLight },

  scrubRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 10, alignSelf: 'stretch' },
  scrubTime: { fontSize: 10, color: '#94A3B8', fontWeight: '700', width: 34, textAlign: 'center' },
  scrubTrack: { flex: 1, height: 6, backgroundColor: '#334155', borderRadius: 3, overflow: 'hidden' },
  scrubFill: { height: 6, backgroundColor: C.primaryLight, borderRadius: 3 },

  filterCard: { width: 72, alignItems: 'center', backgroundColor: C.card, borderRadius: 12, padding: 10, borderWidth: 1, borderColor: C.border },
  filterCardActive: { backgroundColor: C.primary, borderColor: C.primary },
  filterSwatch: { width: 34, height: 34, borderRadius: 17 },
  filterName: { fontSize: 10.5, fontWeight: '700', color: C.text, marginTop: 6 },

  panel: { backgroundColor: C.card, borderRadius: 12, padding: 12, borderWidth: 1, borderColor: C.border },
  stepperRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 7 },
  stepperLabel: { fontSize: 13, fontWeight: '600', color: C.text },
  stepperControls: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  stepperBtn: { width: 28, height: 28, borderRadius: 8, backgroundColor: '#EFF4FF', alignItems: 'center', justifyContent: 'center' },
  stepperValue: { fontSize: 13, fontWeight: '800', color: C.primary, minWidth: 48, textAlign: 'center' },

  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8 },
  toggleTrack: { width: 42, height: 24, borderRadius: 12, backgroundColor: '#CBD5E1', padding: 3 },
  toggleThumb: { width: 18, height: 18, borderRadius: 9, backgroundColor: '#fff' },

  hint: { fontSize: 11, color: C.sub, marginTop: 8, lineHeight: 16 },

  overlayCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 12, padding: 12, marginTop: 8, borderWidth: 1, borderColor: C.border, gap: 4 },
  overlayText: { fontSize: 13.5, fontWeight: '700' },
  overlayMeta: { fontSize: 10.5, color: C.sub, marginTop: 2 },

  trackCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 12, padding: 12, marginTop: 8, borderWidth: 1, borderColor: C.border },
  trackCardActive: { backgroundColor: C.primary, borderColor: C.primary },
  trackIcon: { width: 34, height: 34, borderRadius: 10, backgroundColor: '#EFF4FF', alignItems: 'center', justifyContent: 'center' },
  trackName: { fontSize: 13.5, fontWeight: '700', color: C.text },
  trackMeta: { fontSize: 11, color: C.sub, marginTop: 1 },

  aiBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#FCE7F3', borderRadius: 10, paddingVertical: 10 },
  aiBtnText: { fontSize: 12, fontWeight: '800', color: C.accent },

  summary: { backgroundColor: C.card, borderRadius: 12, padding: 12, borderWidth: 1, borderColor: C.border },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5 },
  summaryLabel: { fontSize: 12, color: C.sub, fontWeight: '600' },
  summaryValue: { fontSize: 12, color: C.text, fontWeight: '700', maxWidth: '60%' },

  exportProgressWrap: { marginTop: 14, backgroundColor: C.darkCard, borderRadius: 12, padding: 14 },
  exportProgressText: { fontSize: 12.5, fontWeight: '800', color: '#fff', marginBottom: 8, textAlign: 'center' },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', padding: 22 },
  modalBox: { backgroundColor: C.card, borderRadius: 16, padding: 18 },
  modalTitle: { fontSize: 16, fontWeight: '800', color: C.text },
  modalSub: { fontSize: 12, color: C.sub, marginTop: 3 },
  modalBtnRow: { flexDirection: 'row', gap: 10, marginTop: 16 },
  modalCancel: { flex: 1, alignItems: 'center', paddingVertical: 11, borderRadius: 10, backgroundColor: '#F1F5F9' },
  modalCancelText: { fontSize: 13.5, fontWeight: '700', color: C.sub },
  modalOk: { flex: 1, alignItems: 'center', paddingVertical: 11, borderRadius: 10, backgroundColor: C.primary },
  modalOkText: { fontSize: 13.5, fontWeight: '800', color: '#fff' },

  colorDot: { width: 28, height: 28, borderRadius: 14, borderWidth: 1, borderColor: C.border },
  colorDotActive: { borderWidth: 3, borderColor: C.primaryLight },
});

export default VideoEditorScreen;
